/* Operasyon Merkezi — v2 (departman bazlı ayrı sayfalar + raporlar + odalar)
 * Rotalar: /operations/<slug> — order-taker | on-buro | teknik | kat-hizmetleri | servis | sikayet | raporlar
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    Loader2, Plus, MessageSquare, ClipboardList, Send, UserCheck, Wrench, Sparkles, Utensils,
    AlertTriangle, CheckCircle2, Clock, FileText, Search, BedDouble, Filter, X,
    TrendingUp, TrendingDown, Minus, Download, BarChart3, Users, Camera, Image as ImageIcon, Trash2, Mail, ClipboardCheck, Archive
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';

// ----- sabitler -----
const PRIORITY_COLORS = {
    acil: 'bg-red-100 text-red-700 border-red-300',
    yuksek: 'bg-orange-100 text-orange-700 border-orange-300',
    normal: 'bg-blue-100 text-blue-700 border-blue-300',
    dusuk: 'bg-slate-100 text-slate-600 border-slate-300',
};
const STATUS_COLORS = {
    yeni: 'bg-amber-100 text-amber-800',
    atandi: 'bg-indigo-100 text-indigo-700',
    ustlenildi: 'bg-purple-100 text-purple-700',
    islemde: 'bg-blue-100 text-blue-700',
    beklemede: 'bg-yellow-100 text-yellow-800',
    cozuldu: 'bg-emerald-100 text-emerald-700',
    tamamlandi: 'bg-emerald-100 text-emerald-700',
    kapandi: 'bg-slate-200 text-slate-700',
    iptal: 'bg-slate-100 text-slate-500',
};
const STATUS_LABELS = { yeni: 'Yeni', atandi: 'Atandı', ustlenildi: 'Üstlenildi', islemde: 'İşlemde', beklemede: 'Beklemede', cozuldu: 'Çözüldü', tamamlandi: 'Tamamlandı', kapandi: 'Kapandı', iptal: 'İptal' };
const TYPE_LABELS = { talep: 'Talep', sikayet: 'Şikayet', bakim: 'Bakım' };
const DEP_LABELS = { teknik: 'Teknik', on_buro: 'Ön Büro', kat_hizmetleri: 'Kat Hizmetleri', servis: 'Servis' };
const OPEN_STATES = ['yeni', 'atandi', 'ustlenildi', 'islemde', 'beklemede'];

const ROOM_STATUSES = [
    { value: 'temiz', label: 'Temiz', color: 'bg-emerald-500', text: 'text-white' },
    { value: 'kirli', label: 'Kirli', color: 'bg-red-500', text: 'text-white' },
    { value: 'temizleniyor', label: 'Temizleniyor', color: 'bg-yellow-400', text: 'text-slate-800' },
    { value: 'kontrol', label: 'Kontrol Edildi', color: 'bg-blue-500', text: 'text-white' },
    { value: 'kullanim_disi', label: 'Kullanım Dışı', color: 'bg-slate-400', text: 'text-white' },
];
const ROOM_STATUS_MAP = Object.fromEntries(ROOM_STATUSES.map(s => [s.value, s]));

const DEP_MAP = {
    'order-taker': { value: 'order_taker', label: 'Order-Taker', icon: ClipboardList, kind: 'operator' },
    'on-buro': { value: 'on_buro', label: 'Ön Büro', icon: UserCheck, kind: 'dept' },
    'teknik': { value: 'teknik', label: 'Teknik', icon: Wrench, kind: 'dept' },
    'kat-hizmetleri': { value: 'kat_hizmetleri', label: 'Kat Hizmetleri', icon: Sparkles, kind: 'housekeeping' },
    'servis': { value: 'servis', label: 'Servis', icon: Utensils, kind: 'dept' },
    'sikayet': { value: null, label: 'Şikayet', icon: MessageSquare, kind: 'glitch' },
    'raporlar': { value: null, label: 'Raporlar', icon: FileText, kind: 'reports' },
};

// ----- yardımcılar -----
function minutesSince(iso) { return iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 60000) : 0; }
function fmtMin(m) { if (m == null) return '—'; if (m < 60) return `${Math.round(m)} dk`; const h = Math.floor(m / 60); const mm = Math.round(m % 60); return `${h}s ${mm}dk`; }
function shortId(id) { return id ? id.slice(0, 6).toUpperCase() : '—'; }

// Read-only kullanıcı (ust_yonetici) için disabled UX hook
function useRO() {
    try { return !!useAuth().isReadOnly; } catch { return false; }
}

// ===== Photo Helpers =====
function assetUrl(p) {
    if (!p) return '';
    if (p.startsWith('http://') || p.startsWith('https://') || p.startsWith('data:')) return p;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    return `${base}${p}`;
}

function PhotoInput({ onUploaded, multiple = true, testid = 'photo-input' }) {
    const [busy, setBusy] = useState(false);
    const ro = useRO();
    const upload = async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await api.post('/operations/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            return r.data;
        } catch (e) { toast.error(e?.response?.data?.detail || 'Yükleme başarısız'); return null; }
    };
    const onChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBusy(true);
        const out = [];
        for (const f of files) { const u = await upload(f); if (u) out.push(u); }
        setBusy(false);
        if (out.length) onUploaded(out);
        e.target.value = '';
    };
    return (
        <label className={`inline-flex items-center gap-1 text-xs bg-white border rounded px-2 py-1 ${ro ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`} data-testid={testid}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            <span>Foto ekle</span>
            <input type="file" accept="image/*" capture="environment" multiple={multiple} className="hidden" onChange={onChange} disabled={ro} />
        </label>
    );
}

function PhotoGrid({ photos = [], onRemove }) {
    const [zoom, setZoom] = useState(null);
    if (!photos.length) return null;
    return (
        <>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="photo-grid">
                {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded overflow-hidden border bg-slate-50">
                        {(p.mime || '').startsWith('image/') ? (
                            <img src={assetUrl(p.url)} alt="" className="w-full h-full object-cover cursor-zoom-in" onClick={() => setZoom(p)} />
                        ) : (
                            <a href={assetUrl(p.url)} target="_blank" rel="noreferrer" className="block w-full h-full flex items-center justify-center text-xs text-slate-600"><ImageIcon className="w-5 h-5" />PDF</a>
                        )}
                        {onRemove && (
                            <button onClick={() => onRemove(i)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-1 text-[10px]"><X className="w-3 h-3" /></button>
                        )}
                    </div>
                ))}
            </div>
            {zoom && (
                <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
                    <DialogContent className="max-w-3xl">
                        <DialogHeader><DialogTitle>Önizleme</DialogTitle></DialogHeader>
                        <img src={assetUrl(zoom.url)} alt="" className="w-full" />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}


// ===== Özet Kartları =====
function SummaryCardItem({ icon: Icon, label, value, color, testid }) {
    return (
        <div className={`rounded-lg p-3 border-l-4 ${color} bg-white shadow-sm`} data-testid={testid}>
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Icon className="w-4 h-4" />{label}</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{value}</div>
        </div>
    );
}

function SummaryCards({ tickets }) {
    const stats = useMemo(() => {
        const now = Date.now();
        const today = new Date(); today.setHours(0, 0, 0, 0);
        let open = 0, inprog = 0, overdue = 0, closedToday = 0;
        for (const t of tickets) {
            const s = t.status;
            if (OPEN_STATES.includes(s)) {
                open++;
                if (s === 'islemde') inprog++;
                const c = t.created_at ? new Date(t.created_at).getTime() : 0;
                const elapsed = (now - c) / 60000;
                if (elapsed > (t.sla_target_minutes || 180)) overdue++;
            }
            if (['cozuldu', 'tamamlandi', 'kapandi'].includes(s) && t.done_at && new Date(t.done_at) >= today) closedToday++;
        }
        return { open, inprog, overdue, closedToday };
    }, [tickets]);
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="op-summary-cards">
            <SummaryCardItem icon={Clock} label="Açık" value={stats.open} color="border-amber-500" testid="sum-open" />
            <SummaryCardItem icon={Loader2} label="İşlemde" value={stats.inprog} color="border-blue-500" testid="sum-inprog" />
            <SummaryCardItem icon={AlertTriangle} label="Geciken (SLA)" value={stats.overdue} color="border-red-500" testid="sum-overdue" />
            <SummaryCardItem icon={CheckCircle2} label="Bugün Kapanan" value={stats.closedToday} color="border-emerald-500" testid="sum-closed" />
        </div>
    );
}

// ===== Filtre Barı =====
function FilterBar({ filters, setFilters, showDep = false }) {
    const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
    return (
        <div className="flex flex-wrap items-center gap-2 bg-white p-2 rounded-lg border shadow-sm" data-testid="op-filterbar">
            <div className="relative flex-1 min-w-[160px]">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input value={filters.q || ''} onChange={(e) => set('q', e.target.value)} placeholder="Ara (konu / oda / açıklama)" className="pl-8 h-9" data-testid="op-filter-q" />
            </div>
            <Select value={filters.status || 'all'} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="w-36 h-9" data-testid="op-filter-status"><SelectValue placeholder="Durum" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="open">Tüm Açıklar</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={filters.priority || 'all'} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger className="w-32 h-9" data-testid="op-filter-priority"><SelectValue placeholder="Öncelik" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">Tüm Öncelikler</SelectItem>
                    <SelectItem value="acil">Acil</SelectItem>
                    <SelectItem value="yuksek">Yüksek</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="dusuk">Düşük</SelectItem>
                </SelectContent>
            </Select>
            {showDep && (
                <Select value={filters.department || 'all'} onValueChange={(v) => set('department', v)}>
                    <SelectTrigger className="w-36 h-9" data-testid="op-filter-dep"><SelectValue placeholder="Departman" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tüm Departmanlar</SelectItem>
                        {Object.entries(DEP_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                </Select>
            )}
            <Button variant="ghost" size="sm" className="h-9" onClick={() => setFilters({})} data-testid="op-filter-clear"><X className="w-3 h-3 mr-1" /> Temizle</Button>
        </div>
    );
}

function applyFilters(items, filters) {
    return items.filter(r => {
        if (filters.status === 'open' && !OPEN_STATES.includes(r.status)) return false;
        if (filters.status && filters.status !== 'all' && filters.status !== 'open' && r.status !== filters.status) return false;
        if (filters.priority && filters.priority !== 'all' && r.priority !== filters.priority) return false;
        if (filters.department && filters.department !== 'all' && r.department !== filters.department) return false;
        if (filters.q) {
            const q = filters.q.toLocaleLowerCase('tr-TR');
            const hay = `${r.location || ''} ${r.description || ''} ${r.category || ''}`.toLocaleLowerCase('tr-TR');
            if (!hay.includes(q)) return false;
        }
        return true;
    });
}

// ===== Bilet Tablosu =====
function TicketTable({ items, onOpen, emptyText = 'Kayıt yok' }) {
    if (!items.length) {
        return (
            <div className="text-center py-10 bg-white rounded-lg border border-dashed text-slate-400" data-testid="op-empty">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <div className="text-sm">{emptyText}</div>
            </div>
        );
    }
    return (
        <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="op-table">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                        <tr>
                            <th className="px-3 py-2 text-left">No</th>
                            <th className="px-3 py-2 text-left">Konu</th>
                            <th className="px-3 py-2 text-left">Konum</th>
                            <th className="px-3 py-2 text-left">Öncelik</th>
                            <th className="px-3 py-2 text-left">Durum</th>
                            <th className="px-3 py-2 text-left">Departman</th>
                            <th className="px-3 py-2 text-left">Açan</th>
                            <th className="px-3 py-2 text-left">Süre / SLA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(r => {
                            const elapsed = minutesSince(r.created_at);
                            const slaBreach = OPEN_STATES.includes(r.status) && elapsed > (r.sla_target_minutes || 180);
                            return (
                                <tr key={r.id} onClick={() => onOpen(r)} className={`border-t hover:bg-slate-50 cursor-pointer ${slaBreach ? 'bg-red-50/40' : ''}`} data-testid={`op-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-500">#{shortId(r.id)}</td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-800">{r.category}{r.type === 'sikayet' && <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">Şikayet</Badge>}</div>
                                        {r.description && <div className="text-xs text-slate-500 line-clamp-1 max-w-md">{r.description}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">{r.location}</td>
                                    <td className="px-3 py-2"><Badge className={PRIORITY_COLORS[r.priority]}>{r.priority?.toUpperCase()}</Badge></td>
                                    <td className="px-3 py-2"><Badge className={STATUS_COLORS[r.status]}>{STATUS_LABELS[r.status]}</Badge></td>
                                    <td className="px-3 py-2 text-slate-600">{DEP_LABELS[r.department] || r.department}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{r.created_by_name || '—'}<br /><span className="text-slate-400">{r.acan_departman ? DEP_LABELS[r.acan_departman] || r.acan_departman : ''}</span></td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="text-slate-700">{fmtMin(elapsed)}</div>
                                        {slaBreach && <span className="text-red-600 font-bold text-[10px]">SLA AŞIM</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ===== Yeni Talep Formu =====
function NewRequestForm({ meta, onCreated, defaultType = 'talep', acanDep = '', lockedLocation = '', lockedDepartment = '' }) {
    const ro = useRO();
    const [form, setForm] = useState({ location: lockedLocation, category: '', department: lockedDepartment, priority: 'normal', description: '', source: 'personel', type: defaultType, guest_ref: '' });
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const allCats = meta ? Object.values(meta.department_categories || {}).flat() : [];
    const onCat = (c) => setForm(f => ({ ...f, category: c, department: lockedDepartment || meta?.category_to_department?.[c] || f.department }));
    const submit = async () => {
        if (!form.location || !form.category) return toast.error('Lokasyon ve kategori zorunlu');
        if (!form.department) return toast.error('Sorumlu departman seçiniz');
        if (acanDep && form.department === acanDep) return toast.error('Açan ve sorumlu departman aynı olamaz — başka bir departmana yönlendirin');
        setBusy(true);
        try {
            const payload = { ...form, photos };
            if (acanDep) payload.acan_departman = acanDep;
            await api.post('/operations/requests', payload);
            toast.success(form.type === 'sikayet' ? 'Şikayet oluşturuldu' : 'Talep oluşturuldu');
            setForm({ location: lockedLocation, category: '', department: lockedDepartment, priority: 'normal', description: '', source: 'personel', type: defaultType, guest_ref: '' });
            setPhotos([]);
            onCreated && onCreated();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
        finally { setBusy(false); }
    };
    return (
        <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-600" />Yeni {form.type === 'sikayet' ? 'Şikayet' : 'Talep'} Aç</CardTitle></CardHeader>
            <CardContent className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger data-testid="op-form-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="talep">Talep</SelectItem>
                            <SelectItem value="sikayet">Şikayet</SelectItem>
                            <SelectItem value="bakim">Bakım</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Lokasyon (oda/alan)" data-testid="op-form-location" disabled={!!lockedLocation} />
                    <Select value={form.category} onValueChange={onCat}>
                        <SelectTrigger data-testid="op-form-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
                        <SelectContent>{allCats.map((c, i) => <SelectItem key={`${c.value}-${i}`} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} disabled={!!lockedDepartment}>
                        <SelectTrigger data-testid="op-form-dep"><SelectValue placeholder="Sorumlu Dep." /></SelectTrigger>
                        <SelectContent>{(meta?.departments || []).map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger data-testid="op-form-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>{(meta?.priorities || []).map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={form.guest_ref} onChange={(e) => setForm({ ...form, guest_ref: e.target.value })} placeholder="Misafir/Oda (ops.)" data-testid="op-form-guest" />
                </div>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Açıklama" data-testid="op-form-desc" />
                <div className="flex items-center gap-2 flex-wrap">
                    <PhotoInput onUploaded={(arr) => setPhotos(p => [...p, ...arr])} testid="op-form-photo" />
                    <PhotoGrid photos={photos} onRemove={(i) => setPhotos(p => p.filter((_, idx) => idx !== i))} />
                </div>
                <Button onClick={submit} disabled={busy || ro} className="bg-emerald-600 hover:bg-emerald-700" data-testid="op-form-submit">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Oluştur
                </Button>
            </CardContent>
        </Card>
    );
}

// ===== Bilet Detay Modalı =====
function TicketDetail({ ticket, onClose, onChanged }) {
    const ro = useRO();
    const [reply, setReply] = useState('');
    const [glitch, setGlitch] = useState({
        root_cause: ticket.root_cause || '', resolution: ticket.resolution || '',
        guest_recovery: ticket.guest_recovery || '', follow_up: ticket.follow_up || '',
    });
    const [replyPhotos, setReplyPhotos] = useState([]);
    const sendReply = async () => {
        if (!reply.trim() && replyPhotos.length === 0) return;
        try {
            await api.post(`/operations/requests/${ticket.id}/reply`, { message: reply, photos: replyPhotos });
            setReply('');
            setReplyPhotos([]);
            toast.success('Mesaj eklendi');
            onChanged && onChanged();
        } catch { toast.error('Mesaj gönderilemedi'); }
    };
    const addTicketPhotos = async (arr) => {
        try {
            await api.post(`/operations/requests/${ticket.id}/photos`, { photos: arr });
            toast.success('Foto eklendi');
            onChanged && onChanged();
        } catch { toast.error('Foto eklenemedi'); }
    };
    const setStatus = async (s) => {
        try { await api.post(`/operations/requests/${ticket.id}/status`, { status: s }); toast.success('Durum güncellendi'); onChanged && onChanged(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
    };
    const saveGlitch = async () => {
        try { await api.put(`/operations/requests/${ticket.id}`, glitch); toast.success('Şikayet alanları kaydedildi'); onChanged && onChanged(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
    };
    return (
        <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`ticket-detail-${ticket.id}`} aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-500">#{shortId(ticket.id)}</span>
                        <Badge className={PRIORITY_COLORS[ticket.priority]}>{ticket.priority?.toUpperCase()}</Badge>
                        <Badge className={STATUS_COLORS[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
                        <span>{TYPE_LABELS[ticket.type] || 'Talep'} · {ticket.location}</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="text-slate-600 text-xs">
                        <b>Sorumlu:</b> {DEP_LABELS[ticket.department] || ticket.department} · <b>Kategori:</b> {ticket.category}
                        {ticket.acan_departman && <> · <b>Açan:</b> {DEP_LABELS[ticket.acan_departman] || ticket.acan_departman}</>}
                        {ticket.guest_ref && <> · <b>Misafir:</b> {ticket.guest_ref}</>}
                    </div>
                    <div className="bg-slate-50 p-2 rounded text-slate-700 whitespace-pre-wrap">{ticket.description || '-'}</div>
                    {(ticket.photos && ticket.photos.length > 0) && <PhotoGrid photos={ticket.photos} />}
                    <div className="flex items-center gap-2">
                        <PhotoInput onUploaded={addTicketPhotos} testid="ticket-add-photo" />
                        <span className="text-[10px] text-slate-400">Bilete foto/dosya ekle</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {['islemde', 'beklemede', 'cozuldu', 'kapandi', 'iptal'].map(s => (
                            <Button key={s} size="sm" variant="outline" disabled={ro} className="h-7 text-xs" onClick={() => setStatus(s)} data-testid={`status-${s}`}>{STATUS_LABELS[s]}</Button>
                        ))}
                    </div>
                    <div>
                        <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Yazışma</div>
                        <div className="max-h-60 overflow-y-auto space-y-2 bg-slate-50 p-2 rounded" data-testid="ticket-thread">
                            {(ticket.thread || []).length === 0 && <div className="text-xs text-slate-400">Henüz mesaj yok</div>}
                            {(ticket.thread || []).map((t, i) => (
                                <div key={i} className="text-xs">
                                    <span className="font-semibold text-emerald-700">{t.by_name}</span>
                                    {t.departman && <span className="text-slate-400"> · {t.departman}</span>}
                                    <span className="text-slate-400"> · {new Date(t.at).toLocaleString('tr-TR')}</span>
                                    <div className="ml-2 mt-0.5 text-slate-700 whitespace-pre-wrap">{t.message}</div>
                                    {t.photos && t.photos.length > 0 && <div className="ml-2"><PhotoGrid photos={t.photos} /></div>}
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-1 mt-2">
                            <div className="flex gap-1">
                                <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Yanıt yaz..." onKeyDown={(e) => e.key === 'Enter' && sendReply()} data-testid="ticket-reply-input" />
                                <Button size="sm" onClick={sendReply} disabled={ro} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ticket-reply-send"><Send className="w-3 h-3" /></Button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <PhotoInput onUploaded={(arr) => setReplyPhotos(p => [...p, ...arr])} testid="ticket-reply-photo" />
                                <PhotoGrid photos={replyPhotos} onRemove={(i) => setReplyPhotos(p => p.filter((_, idx) => idx !== i))} />
                            </div>
                        </div>
                    </div>
                    {ticket.type === 'sikayet' && (
                        <div className="border-t pt-2 space-y-2" data-testid="glitch-fields">
                            <div className="font-semibold text-slate-700">Şikayet (Glitch) Detayları</div>
                            <Textarea placeholder="Kök Neden (Root Cause)" value={glitch.root_cause} onChange={(e) => setGlitch({ ...glitch, root_cause: e.target.value })} className="text-xs" />
                            <Textarea placeholder="Çözüm (Resolution)" value={glitch.resolution} onChange={(e) => setGlitch({ ...glitch, resolution: e.target.value })} className="text-xs" />
                            <Textarea placeholder="Misafir Telafisi (Guest Recovery)" value={glitch.guest_recovery} onChange={(e) => setGlitch({ ...glitch, guest_recovery: e.target.value })} className="text-xs" />
                            <Textarea placeholder="Takip / Follow-up" value={glitch.follow_up} onChange={(e) => setGlitch({ ...glitch, follow_up: e.target.value })} className="text-xs" />
                            <Button size="sm" onClick={saveGlitch} disabled={ro} className="bg-amber-600 hover:bg-amber-700" data-testid="glitch-save">Kaydet</Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ===== Analiz & Departman Değerlendirme Paneli =====
const PERF_BADGE = { iyi: 'bg-emerald-100 text-emerald-700 border border-emerald-300', orta: 'bg-amber-100 text-amber-700 border border-amber-300', zayif: 'bg-red-100 text-red-700 border border-red-300', na: 'bg-slate-100 text-slate-500 border border-slate-200' };
const PERF_LBL = { iyi: 'İYİ', orta: 'ORTA', zayif: 'ZAYIF', na: '—' };

function DeltaBadge({ value, invert = false }) {
    if (value == null) return <span className="text-slate-400 text-[10px]">—</span>;
    const positive = value > 0;
    const better = invert ? !positive : positive;
    if (value === 0) return <span className="text-slate-500 text-[10px] inline-flex items-center"><Minus className="w-3 h-3" /> 0%</span>;
    return (
        <span className={`text-[10px] inline-flex items-center font-medium ${better ? 'text-emerald-600' : 'text-red-600'}`}>
            {positive ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
            {Math.abs(value).toFixed(1)}%
        </span>
    );
}

function KpiCard({ label, value, delta, suffix = '', invert = false, color = 'border-slate-300', testid }) {
    return (
        <div className={`bg-white rounded-lg p-3 border-l-4 shadow-sm ${color}`} data-testid={testid}>
            <div className="text-xs text-slate-500">{label}</div>
            <div className="flex items-baseline gap-2 mt-1">
                <div className="text-2xl font-bold text-slate-800">{value ?? '—'}{value != null && suffix}</div>
                <DeltaBadge value={delta} invert={invert} />
            </div>
        </div>
    );
}

function AnalyticsPanel() {
    const [period, setPeriod] = useState('today');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { period };
            if (period === 'range') { params.start = start; params.end = end; }
            const r = await api.get('/operations/analytics', { params });
            setData(r.data);
        } catch { toast.error('Analiz verisi alınamadı'); }
        finally { setLoading(false); }
    }, [period, start, end]);

    useEffect(() => { if (period !== 'range') load(); }, [period, load]);

    const downloadPdf = async () => {
        try {
            const params = { period };
            if (period === 'range') { params.start = start; params.end = end; }
            const r = await api.get('/operations/analytics/pdf', { params, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url; a.download = `operasyon-analiz-${period}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF indirildi');
        } catch { toast.error('PDF indirilemedi'); }
    };

    const k = data?.kpis || {};
    const d = data?.kpis_delta || {};

    return (
        <Card data-testid="ops-analytics">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-indigo-600" />Analiz & Departman Değerlendirme</CardTitle>
                    <div className="flex items-center gap-1 flex-wrap">
                        {['today', 'week', 'month', 'range'].map(p => (
                            <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} className={`h-8 ${period === p ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`} onClick={() => setPeriod(p)} data-testid={`an-period-${p}`}>
                                {{ today: 'Bugün', week: 'Bu Hafta', month: 'Bu Ay', range: 'Tarih Aralığı' }[p]}
                            </Button>
                        ))}
                        {period === 'range' && (
                            <>
                                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 w-36" data-testid="an-range-start" />
                                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 w-36" data-testid="an-range-end" />
                                <Button size="sm" onClick={load} className="h-8 bg-indigo-600 hover:bg-indigo-700" data-testid="an-range-apply">Uygula</Button>
                            </>
                        )}
                        <Button size="sm" variant="outline" className="h-8" onClick={downloadPdf} data-testid="an-pdf"><Download className="w-3 h-3 mr-1" />PDF</Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {loading && <Loader2 className="w-5 h-5 animate-spin mx-auto my-4" />}
                {!loading && data && (
                    <>
                        {/* A) KPI kartları */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="an-kpis">
                            <KpiCard label="Toplam" value={k.total} delta={d.total} color="border-slate-400" testid="an-kpi-total" />
                            <KpiCard label="Açık" value={k.open} delta={d.open} invert color="border-amber-500" testid="an-kpi-open" />
                            <KpiCard label="Geciken (SLA)" value={k.overdue} delta={d.overdue} invert color="border-red-500" testid="an-kpi-overdue" />
                            <KpiCard label="Kapanan" value={k.closed} delta={d.closed} color="border-emerald-500" testid="an-kpi-closed" />
                            <KpiCard label="Ort. Yanıt (dk)" value={k.avg_response_min} delta={d.avg_response_min} invert color="border-blue-500" testid="an-kpi-resp" />
                            <KpiCard label="Ort. Çözüm (dk)" value={k.avg_resolution_min} delta={d.avg_resolution_min} invert color="border-purple-500" testid="an-kpi-resol" />
                            <KpiCard label="SLA Uyum" value={k.sla_compliance_pct} delta={d.sla_compliance_pct} suffix="%" color="border-emerald-600" testid="an-kpi-sla" />
                        </div>

                        {/* B) Departman tablosu */}
                        <div>
                            <div className="text-sm font-semibold text-slate-700 mb-1">Departman Değerlendirme</div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="an-dep-table">
                                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Departman</th>
                                            <th className="px-2 py-2">Toplam</th>
                                            <th className="px-2 py-2">Açık</th>
                                            <th className="px-2 py-2">Kapalı</th>
                                            <th className="px-2 py-2">İptal</th>
                                            <th className="px-2 py-2">Ort. Yanıt</th>
                                            <th className="px-2 py-2">Ort. Çözüm</th>
                                            <th className="px-2 py-2">SLA %</th>
                                            <th className="px-2 py-2">Geciken</th>
                                            <th className="px-2 py-2">Performans</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.departments || []).map(r => (
                                            <tr key={r.department} className="border-t" data-testid={`an-dep-${r.department}`}>
                                                <td className="px-3 py-2 font-semibold">{DEP_LABELS[r.department] || r.department}</td>
                                                <td className="px-2 py-2 text-center">{r.total}</td>
                                                <td className="px-2 py-2 text-center text-amber-700 font-medium">{r.open}</td>
                                                <td className="px-2 py-2 text-center text-emerald-700 font-medium">{r.closed}</td>
                                                <td className="px-2 py-2 text-center text-slate-500">{r.cancelled}</td>
                                                <td className="px-2 py-2 text-center">{fmtMin(r.avg_response_min)}</td>
                                                <td className="px-2 py-2 text-center">{fmtMin(r.avg_resolution_min)}</td>
                                                <td className="px-2 py-2 text-center">{r.sla_compliance_pct == null ? '—' : <span className={`font-bold ${r.sla_compliance_pct >= 85 ? 'text-emerald-700' : r.sla_compliance_pct >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{r.sla_compliance_pct}%</span>}</td>
                                                <td className="px-2 py-2 text-center text-red-700 font-medium">{r.overdue}</td>
                                                <td className="px-2 py-2 text-center"><Badge className={PERF_BADGE[r.perf]}>{PERF_LBL[r.perf]}</Badge></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* C) Trend grafiği */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <div className="bg-white border rounded-lg p-2" data-testid="an-trend">
                                <div className="text-xs font-semibold text-slate-600 mb-1">Trend: İş Hacmi & Ort. Çözüm Süresi</div>
                                {(data.trend || []).length === 0 ? (
                                    <div className="text-xs text-slate-400 text-center py-6">Bu dönemde kayıt yok</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={200}>
                                        <LineChart data={data.trend}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" fontSize={10} />
                                            <YAxis yAxisId="left" fontSize={10} />
                                            <YAxis yAxisId="right" orientation="right" fontSize={10} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 10 }} />
                                            <Line yAxisId="left" type="monotone" dataKey="count" stroke="#4f46e5" name="İş Sayısı" />
                                            <Line yAxisId="right" type="monotone" dataKey="avg_resolution_min" stroke="#10b981" name="Ort. Çözüm (dk)" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                            <div className="bg-white border rounded-lg p-2" data-testid="an-hour-dist">
                                <div className="text-xs font-semibold text-slate-600 mb-1">Saatlik Talep Yoğunluğu</div>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={data.insights?.hour_distribution || []}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="hour" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#6366f1" name="Talep Sayısı" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* D) Personel performansı */}
                        <div>
                            <div className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1"><Users className="w-4 h-4" /> Personel Performansı</div>
                            {(data.staff || []).length === 0 ? (
                                <div className="text-xs text-slate-400 bg-slate-50 rounded p-3 text-center">Atanmış personel kaydı yok</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm" data-testid="an-staff-table">
                                        <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Personel</th>
                                                <th className="px-3 py-2">Üstlendi</th>
                                                <th className="px-3 py-2">Kapadı</th>
                                                <th className="px-3 py-2">Ort. Çözüm</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.staff.slice(0, 15).map(s => (
                                                <tr key={s.user_id} className="border-t">
                                                    <td className="px-3 py-2">{s.name}</td>
                                                    <td className="px-3 py-2 text-center">{s.taken}</td>
                                                    <td className="px-3 py-2 text-center text-emerald-700 font-medium">{s.closed}</td>
                                                    <td className="px-3 py-2 text-center">{fmtMin(s.avg_resolution_min)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* E) İçgörüler */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="an-insights">
                            <Card><CardHeader className="pb-1"><CardTitle className="text-sm">En Çok İş Alan Alan (Top 5)</CardTitle></CardHeader>
                                <CardContent className="text-xs space-y-1">
                                    {(data.insights?.top_locations || []).map(x => (<div key={x.name} className="flex justify-between"><span className="text-slate-700">{x.name}</span><Badge className="bg-slate-100 text-slate-700">{x.count}</Badge></div>))}
                                    {!data.insights?.top_locations?.length && <div className="text-slate-400">Veri yok</div>}
                                </CardContent></Card>
                            <Card><CardHeader className="pb-1"><CardTitle className="text-sm">En Sık Kategori (Top 5)</CardTitle></CardHeader>
                                <CardContent className="text-xs space-y-1">
                                    {(data.insights?.top_categories || []).map(x => (<div key={x.name} className="flex justify-between"><span className="text-slate-700">{x.name}</span><Badge className="bg-slate-100 text-slate-700">{x.count}</Badge></div>))}
                                    {!data.insights?.top_categories?.length && <div className="text-slate-400">Veri yok</div>}
                                </CardContent></Card>
                            <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Şikayet & Misafir Telafisi</CardTitle></CardHeader>
                                <CardContent className="text-xs space-y-1">
                                    <div className="flex justify-between"><span>Toplam Şikayet</span><b>{data.insights?.glitch_total ?? 0}</b></div>
                                    <div className="flex justify-between"><span>Çözüm Oranı</span><b className="text-emerald-700">{data.insights?.glitch_resolution_pct ?? '—'}%</b></div>
                                    <div className="flex justify-between"><span>Telafi Oranı (Recovery)</span><b className="text-amber-700">{data.insights?.glitch_recovery_pct ?? '—'}%</b></div>
                                </CardContent></Card>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

// ===== Rapor E-posta Alıcıları =====
function ReportEmailsCard({ analyticsPeriod = 'week' }) {
    const ro = useRO();
    const [emails, setEmails] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const load = useCallback(async () => {
        try { const r = await api.get('/operations/report-emails'); setEmails(r.data?.emails || []); } catch { /* */ }
    }, []);
    useEffect(() => { load(); }, [load]);
    const save = async (next) => {
        try { await api.put('/operations/report-emails', { emails: next }); setEmails(next); toast.success('Liste güncellendi'); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
    };
    const add = () => {
        const v = (input || '').trim();
        if (!v || !v.includes('@')) return toast.error('Geçerli e-posta girin');
        if (emails.includes(v)) return;
        save([...emails, v]);
        setInput('');
    };
    const remove = (e) => save(emails.filter(x => x !== e));
    const send = async () => {
        if (!emails.length) return toast.error('Önce alıcı ekleyin');
        setSending(true);
        try {
            await api.post('/operations/analytics/email', null, { params: { period: analyticsPeriod } });
            toast.success('Rapor e-posta ile gönderildi');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Gönderilemedi'); }
        finally { setSending(false); }
    };
    return (
        <Card data-testid="ops-report-emails">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-600" />Rapor E-posta Alıcıları</CardTitle></CardHeader>
            <CardContent className="space-y-2">
                <div className="flex gap-1">
                    <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="ornek@grandkaptanhotel.com" data-testid="rem-input" onKeyDown={(e) => e.key === 'Enter' && add()} />
                    <Button size="sm" onClick={add} disabled={ro} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rem-add"><Plus className="w-3 h-3" /> Ekle</Button>
                </div>
                <div className="flex flex-wrap gap-1" data-testid="rem-list">
                    {emails.length === 0 && <span className="text-xs text-slate-400">Henüz alıcı yok</span>}
                    {emails.map(e => (
                        <Badge key={e} className="bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                            <span>{e}</span>
                            <button onClick={() => remove(e)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                        </Badge>
                    ))}
                </div>
                <Button size="sm" onClick={send} disabled={sending || ro} className="bg-amber-600 hover:bg-amber-700" data-testid="rem-send">
                    {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Raporu Gönder ({analyticsPeriod === 'today' ? 'Bugün' : analyticsPeriod === 'week' ? 'Bu Hafta' : analyticsPeriod === 'month' ? 'Bu Ay' : 'Aralık'})
                </Button>
            </CardContent>
        </Card>
    );
}

// ===== Mesaj/Duyuru Composer =====
function MessageComposer() {
    const ro = useRO();
    const [toType, setToType] = useState('department');
    const [toValue, setToValue] = useState('on_buro');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [busy, setBusy] = useState(false);
    const [inbox, setInbox] = useState({ items: [], unread: 0 });
    const loadInbox = useCallback(async () => {
        try { const r = await api.get('/operations/messages', { params: { box: 'inbox' } }); setInbox(r.data || { items: [] }); } catch { /* */ }
    }, []);
    useEffect(() => { loadInbox(); }, [loadInbox]);
    const send = async () => {
        if (!subject.trim() || !body.trim()) return toast.error('Konu ve mesaj zorunlu');
        if (!toValue) return toast.error('Alıcı seçin');
        setBusy(true);
        try {
            await api.post('/operations/messages', { to_type: toType, to_value: toValue, subject, body });
            toast.success('Mesaj gönderildi');
            setSubject(''); setBody('');
            loadInbox();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
        finally { setBusy(false); }
    };
    return (
        <Card data-testid="ops-messages">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4 text-emerald-600" />Mesaj / Duyuru</CardTitle></CardHeader>
            <CardContent className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Select value={toType} onValueChange={(v) => { setToType(v); setToValue(v === 'role' ? 'ust_yonetici' : 'on_buro'); }}>
                        <SelectTrigger data-testid="msg-totype"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="department">Departman</SelectItem>
                            <SelectItem value="role">Rol (Üst Yönetim)</SelectItem>
                        </SelectContent>
                    </Select>
                    {toType === 'department' ? (
                        <Select value={toValue} onValueChange={setToValue}>
                            <SelectTrigger data-testid="msg-tovalue"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(DEP_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Select value={toValue} onValueChange={setToValue}>
                            <SelectTrigger data-testid="msg-tovalue"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ust_yonetici">Üst Yönetim</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="yonetici">Yönetici</SelectItem>
                                <SelectItem value="personel">Personel</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Konu" data-testid="msg-subject" />
                </div>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Mesaj" data-testid="msg-body" />
                <Button size="sm" onClick={send} disabled={busy || ro} className="bg-emerald-600 hover:bg-emerald-700" data-testid="msg-send">{busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}Gönder</Button>
                <div className="border-t pt-2">
                    <div className="text-xs font-semibold text-slate-600 mb-1">Gelen Kutusu ({inbox.items?.length || 0}, okunmamış {inbox.unread})</div>
                    <div className="max-h-40 overflow-y-auto text-xs space-y-1" data-testid="msg-inbox">
                        {(inbox.items || []).slice(0, 20).map(m => (
                            <div key={m.id} className="border-b py-1">
                                <div className="font-medium text-slate-700">{m.subject}</div>
                                <div className="text-slate-500">{m.from_name} · {new Date(m.sent_at).toLocaleString('tr-TR')}</div>
                                <div className="text-slate-600 whitespace-pre-wrap">{m.body}</div>
                            </div>
                        ))}
                        {!inbox.items?.length && <div className="text-slate-400 text-center py-2">Mesaj yok</div>}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// ===== Order-Taker Sayfası =====
function OrderTakerView({ meta, onOpenTicket, reloadKey, refresh }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ status: 'open' });
    useEffect(() => {
        setLoading(true);
        api.get('/operations/requests', { params: {} })
            .then(r => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [reloadKey]);
    const filtered = useMemo(() => applyFilters(items, filters), [items, filters]);
    return (
        <div className="space-y-3">
            <SummaryCards tickets={items} />
            <NewRequestForm meta={meta} onCreated={refresh} />
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Filter className="w-4 h-4" />Dağıtım Panosu ({filtered.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <FilterBar filters={filters} setFilters={setFilters} showDep />
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-4" /> : <TicketTable items={filtered} onOpen={onOpenTicket} emptyText="Filtrelere uyan kayıt yok" />}
                </CardContent>
            </Card>
            <AnalyticsPanel />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <MessageComposer />
                <ReportEmailsCard analyticsPeriod="week" />
            </div>
        </div>
    );
}

// ===== Departman Çalışma Alanı (Ön Büro / Teknik / Servis) =====
// ===== Departman KPI Şeridi (küçük özet, tek departman için) =====
function DepartmentKpiBar({ dep }) {
    const [period, setPeriod] = useState('today');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        api.get(`/operations/analytics/department/${dep}`, { params: { period } })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [dep, period]);
    if (!data && !loading) return null;
    const k = data?.kpis || {};
    const d = data?.kpis_delta || {};
    const items = [
        { key: 'total', label: 'Toplam', value: k.total, delta: d.total, color: 'border-slate-400' },
        { key: 'open', label: 'Açık', value: k.open, delta: d.open, invert: true, color: 'border-amber-500' },
        { key: 'overdue', label: 'Geciken', value: k.overdue, delta: d.overdue, invert: true, color: 'border-red-500' },
        { key: 'closed', label: 'Kapanan', value: k.closed, delta: d.closed, color: 'border-emerald-500' },
        { key: 'avg_response_min', label: 'Ort. Yanıt (dk)', value: k.avg_response_min, delta: d.avg_response_min, invert: true, color: 'border-blue-500' },
        { key: 'avg_resolution_min', label: 'Ort. Çözüm (dk)', value: k.avg_resolution_min, delta: d.avg_resolution_min, invert: true, color: 'border-purple-500' },
        { key: 'sla_compliance_pct', label: 'SLA', value: k.sla_compliance_pct, delta: d.sla_compliance_pct, suffix: '%', color: 'border-emerald-600' },
    ];
    return (
        <Card data-testid={`dep-kpi-${dep}`}>
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-indigo-600" />{DEP_LABELS[dep] || dep} — KPI Özeti</CardTitle>
                    <div className="flex items-center gap-1">
                        {['today', 'week', 'month'].map(p => (
                            <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} className={`h-7 px-2 text-xs ${period === p ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`} onClick={() => setPeriod(p)} data-testid={`dep-kpi-period-${p}`}>
                                {{ today: 'Bugün', week: 'Hafta', month: 'Ay' }[p]}
                            </Button>
                        ))}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto my-3" /> : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                        {items.map(it => <KpiCard key={it.key} label={it.label} value={it.value} delta={it.delta} invert={it.invert} suffix={it.suffix || ''} color={it.color} testid={`dep-kpi-${dep}-${it.key}`} />)}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function DepartmentView({ dep, meta, onOpenTicket, reloadKey, refresh, includeRooms = false, roomsContent = null, includeInspections = false, inspectionsContent = null, includeLostFound = false, lostFoundContent = null }) {
    const [view, setView] = useState('gelen'); // gelen | actiklarim | ilgili | odalar | denetim | kayipEsya
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [showForm, setShowForm] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/operations/requests', { params: { view, department: dep, active_only: view !== 'ilgili' } });
            setItems(r.data.items || []);
        } catch { setItems([]); }
        finally { setLoading(false); }
    }, [view, dep]);
    useEffect(() => { if (!['odalar', 'denetim', 'kayipEsya'].includes(view)) load(); }, [load, reloadKey, view]);
    const filtered = useMemo(() => applyFilters(items, filters), [items, filters]);
    return (
        <div className="space-y-3">
            <DepartmentKpiBar dep={dep} />
            <SummaryCards tickets={items} />
            <Tabs value={view} onValueChange={(v) => { setView(v); setShowForm(false); }}>
                <TabsList>
                    <TabsTrigger value="gelen" data-testid="view-is-emri">İş Emri</TabsTrigger>
                    <TabsTrigger value="actiklarim" data-testid="view-talepler">Talepler</TabsTrigger>
                    <TabsTrigger value="ilgili" data-testid="view-ilgili">İlgili Şikayetler</TabsTrigger>
                    {includeRooms && <TabsTrigger value="odalar" data-testid="view-odalar">Odalar</TabsTrigger>}
                    {includeInspections && <TabsTrigger value="denetim" data-testid="view-denetim">Denetim</TabsTrigger>}
                    {includeLostFound && <TabsTrigger value="kayipEsya" data-testid="view-kayip-esya">Kayıp Eşya</TabsTrigger>}
                </TabsList>
            </Tabs>

            {view === 'odalar' ? (
                roomsContent
            ) : view === 'denetim' ? (
                inspectionsContent
            ) : view === 'kayipEsya' ? (
                lostFoundContent
            ) : (
                <>
                    {view === 'actiklarim' && (
                        <div className="flex justify-end">
                            <Button size="sm" onClick={() => setShowForm(!showForm)} variant="outline" data-testid="dep-toggle-form"><Plus className="w-4 h-4 mr-1" /> Yeni Aç</Button>
                        </div>
                    )}
                    {view === 'actiklarim' && showForm && (
                        <NewRequestForm meta={meta} acanDep={dep} onCreated={() => { setShowForm(false); load(); refresh(); }} />
                    )}
                    <FilterBar filters={filters} setFilters={setFilters} />
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-4" /> :
                        <TicketTable
                            items={filtered}
                            onOpen={onOpenTicket}
                            emptyText={view === 'gelen' ? 'Bu departmana atanmış aktif iş emri yok.' : view === 'actiklarim' ? 'Henüz açtığınız bir talep yok.' : 'İlgili şikayet kaydı yok.'} />}
                </>
            )}
        </div>
    );
}

// ===== Kat Hizmetleri Odalar Paneli =====
function HousekeepingRoomsPanel({ meta, refresh }) {
    const ro = useRO();
    const [rooms, setRooms] = useState([]);
    const [summary, setSummary] = useState({});
    const [activeFloor, setActiveFloor] = useState(1);
    const [activeRoom, setActiveRoom] = useState(null);
    const [draft, setDraft] = useState({ status: 'temiz', assignee_name: '', note: '', dnd: false });
    const [busy, setBusy] = useState(false);
    const [showWO, setShowWO] = useState(false);
    const load = useCallback(async () => {
        try {
            const r = await api.get('/operations/rooms/status');
            setRooms(r.data.items || []);
            setSummary(r.data.summary || {});
        } catch { setRooms([]); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const floorRooms = useMemo(() => rooms.filter(r => r.floor === activeFloor), [rooms, activeFloor]);
    const selectRoom = (rn) => {
        const r = rooms.find(x => x.room_no === rn);
        if (!r) return;
        setActiveRoom(rn);
        setDraft({ status: r.status, assignee_name: r.assignee_name || '', note: r.note || '', dnd: !!r.dnd });
        setShowWO(false);
    };
    const save = async () => {
        if (!activeRoom) return;
        setBusy(true);
        try {
            await api.put(`/operations/rooms/${activeRoom}/status`, draft);
            toast.success('Oda durumu kaydedildi');
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-3" data-testid="hk-rooms-panel">
            {/* Sayaçlar */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
                {ROOM_STATUSES.map(s => (
                    <div key={s.value} className="bg-white border rounded p-2 text-center" data-testid={`hk-count-${s.value}`}>
                        <div className={`inline-block w-3 h-3 rounded mr-1 align-middle ${s.color}`}></div>
                        <span className="text-slate-600">{s.label}</span>
                        <div className="text-lg font-bold">{summary[s.value] || 0}</div>
                    </div>
                ))}
                <div className="bg-white border rounded p-2 text-center" data-testid="hk-count-dnd">
                    <div className="inline-block w-3 h-3 rounded mr-1 align-middle bg-red-700"></div>
                    <span className="text-slate-600">DND</span>
                    <div className="text-lg font-bold">{summary.dnd || 0}</div>
                </div>
            </div>

            {/* Kat seçici */}
            <Card>
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        {[1, 2, 3, 4, 5].map(f => (
                            <Button key={f} variant={activeFloor === f ? 'default' : 'outline'} onClick={() => setActiveFloor(f)} className={activeFloor === f ? 'bg-slate-800 hover:bg-slate-900' : ''} data-testid={`hk-floor-${f}`}>{f}. Kat</Button>
                        ))}
                        <div className="ml-auto text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                            {ROOM_STATUSES.map(s => (
                                <span key={s.value} className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${s.color}`}></span> {s.label}</span>
                            ))}
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-700"></span> DND</span>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2" data-testid={`hk-grid-${activeFloor}`}>
                        {floorRooms.map(r => {
                            const cfg = ROOM_STATUS_MAP[r.status] || ROOM_STATUS_MAP.temiz;
                            const cls = r.dnd ? 'bg-red-700 text-white' : `${cfg.color} ${cfg.text}`;
                            const isActive = activeRoom === r.room_no;
                            return (
                                <button
                                    key={r.room_no}
                                    onClick={() => selectRoom(r.room_no)}
                                    className={`relative aspect-square rounded-md border-2 ${cls} font-semibold text-sm transition-all hover:scale-105 hover:shadow-md ${isActive ? 'ring-2 ring-offset-1 ring-blue-600' : ''}`}
                                    data-testid={`hk-room-${r.room_no}`}
                                    title={`${r.room_no} — ${r.dnd ? 'DND' : cfg.label}${r.assignee_name ? ' · ' + r.assignee_name : ''}`}
                                >{r.room_no}</button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Seçili oda paneli */}
            {activeRoom && (
                <Card data-testid="hk-room-editor">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><BedDouble className="w-4 h-4 text-blue-600" /> Oda {activeRoom}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                                <SelectTrigger data-testid="hk-edit-status"><SelectValue /></SelectTrigger>
                                <SelectContent>{ROOM_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input value={draft.assignee_name} onChange={(e) => setDraft({ ...draft, assignee_name: e.target.value })} placeholder="Sorumlu personel" data-testid="hk-edit-assignee" />
                            <label className="flex items-center gap-2 text-sm bg-white rounded px-3 border">
                                <input type="checkbox" checked={draft.dnd} onChange={(e) => setDraft({ ...draft, dnd: e.target.checked })} data-testid="hk-edit-dnd" />
                                DND
                            </label>
                        </div>
                        <Textarea value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Not" data-testid="hk-edit-note" />
                        <div className="flex gap-2 flex-wrap">
                            <Button onClick={save} disabled={busy || ro} className="bg-emerald-600 hover:bg-emerald-700" data-testid="hk-edit-save">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Kaydet</Button>
                            <Button variant="outline" disabled={ro} onClick={() => setShowWO(!showWO)} data-testid="hk-edit-wo-toggle"><Plus className="w-4 h-4 mr-1" /> Bu Oda İçin Yeni İş Emri Aç</Button>
                        </div>
                        {showWO && (
                            <NewRequestForm
                                meta={meta}
                                acanDep="kat_hizmetleri"
                                lockedLocation={`Oda ${activeRoom}`}
                                onCreated={() => { setShowWO(false); refresh(); }}
                            />
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// ===== Kat Hizmetleri Denetim Paneli =====
const INSP_ITEMS = [
    { value: 'yatak', label: 'Yatak' },
    { value: 'banyo', label: 'Banyo' },
    { value: 'zemin', label: 'Zemin' },
    { value: 'minibar', label: 'Minibar' },
    { value: 'havlu', label: 'Havlu' },
    { value: 'genel', label: 'Genel' },
];

function InspectionPanel() {
    const ro = useRO();
    const [list, setList] = useState([]);
    const [room, setRoom] = useState('');
    const [items, setItems] = useState({});
    const [score, setScore] = useState(90);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get('/operations/inspections'); setList(r.data?.items || []); } catch { /* */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!room.match(/^[1-5][0-6][0-9]$/) && !room.match(/^[1-5]68$/)) return toast.error('Geçerli oda no (101-568)');
        const allFilled = INSP_ITEMS.every(it => items[it.value]);
        if (!allFilled) return toast.error('Tüm maddeleri işaretleyin');
        setBusy(true);
        try {
            await api.post('/operations/inspections', { room_no: room, items, score: Number(score), note });
            toast.success('Denetim kaydedildi');
            setRoom(''); setItems({}); setNote(''); setScore(90);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-3" data-testid="hk-inspection-panel">
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-blue-600" />Yeni Denetim</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Input value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Oda no (101-568)" data-testid="insp-room" />
                        <Input type="number" min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)} placeholder="Puan" data-testid="insp-score" />
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not" data-testid="insp-note" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {INSP_ITEMS.map(it => (
                            <div key={it.value} className="bg-slate-50 rounded p-2 flex items-center justify-between gap-2">
                                <span className="text-sm font-medium">{it.label}</span>
                                <div className="flex gap-1">
                                    <Button size="sm" variant={items[it.value] === 'gecti' ? 'default' : 'outline'} className={`h-7 px-2 text-xs ${items[it.value] === 'gecti' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} onClick={() => setItems(s => ({ ...s, [it.value]: 'gecti' }))} data-testid={`insp-${it.value}-pass`}>Geçti</Button>
                                    <Button size="sm" variant={items[it.value] === 'kaldi' ? 'default' : 'outline'} className={`h-7 px-2 text-xs ${items[it.value] === 'kaldi' ? 'bg-red-600 hover:bg-red-700' : ''}`} onClick={() => setItems(s => ({ ...s, [it.value]: 'kaldi' }))} data-testid={`insp-${it.value}-fail`}>Kaldı</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Button onClick={submit} disabled={busy || ro} className="bg-blue-600 hover:bg-blue-700" data-testid="insp-submit">{busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ClipboardCheck className="w-3 h-3 mr-1" />}Kaydet</Button>
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Son Denetimler ({list.length})</CardTitle></CardHeader>
                <CardContent>
                    {list.length === 0 ? <div className="text-xs text-slate-400 text-center py-3">Denetim kaydı yok</div> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="insp-list">
                                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                                    <tr><th className="px-2 py-1 text-left">Oda</th><th>Sonuç</th><th>Puan</th><th>Denetçi</th><th>Tarih</th></tr>
                                </thead>
                                <tbody>
                                    {list.slice(0, 50).map(d => (
                                        <tr key={d.id} className="border-t">
                                            <td className="px-2 py-1 font-mono">{d.room_no}</td>
                                            <td className="px-2 py-1"><Badge className={d.all_pass ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{d.all_pass ? 'GEÇTİ' : 'KALDI'}</Badge></td>
                                            <td className="px-2 py-1 text-center">{d.score ?? '—'}</td>
                                            <td className="px-2 py-1 text-xs text-slate-500">{d.inspector_name}</td>
                                            <td className="px-2 py-1 text-xs text-slate-500">{new Date(d.at).toLocaleString('tr-TR')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ===== Kayıp Eşya Paneli =====
const LOST_STATUS = { depoda: 'Depoda', teslim: 'Teslim Edildi', imha: 'İmha' };
function LostFoundPanel() {
    const ro = useRO();
    const [list, setList] = useState([]);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [form, setForm] = useState({ room_no: '', item: '', finder_name: '', note: '', status: 'depoda', photos: [] });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (q) params.q = q;
            if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
            const r = await api.get('/operations/lost-found', { params });
            setList(r.data?.items || []);
        } catch { /* */ }
    }, [q, statusFilter]);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!form.item.trim()) return toast.error('Eşya adı zorunlu');
        setBusy(true);
        try {
            await api.post('/operations/lost-found', form);
            toast.success('Kayıt eklendi');
            setForm({ room_no: '', item: '', finder_name: '', note: '', status: 'depoda', photos: [] });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Hata'); }
        finally { setBusy(false); }
    };
    const updateStatus = async (id, status) => {
        try { await api.put(`/operations/lost-found/${id}`, { status }); load(); toast.success('Durum güncellendi'); }
        catch { toast.error('Hata'); }
    };

    return (
        <div className="space-y-3" data-testid="hk-lost-panel">
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Archive className="w-4 h-4 text-amber-600" />Yeni Kayıp Eşya</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Input value={form.room_no} onChange={(e) => setForm({ ...form, room_no: e.target.value })} placeholder="Oda no (ops.)" data-testid="lost-room" />
                        <Input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="Eşya" data-testid="lost-item" />
                        <Input value={form.finder_name} onChange={(e) => setForm({ ...form, finder_name: e.target.value })} placeholder="Bulan" data-testid="lost-finder" />
                    </div>
                    <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Not" data-testid="lost-note" />
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger className="w-44" data-testid="lost-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(LOST_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <PhotoInput onUploaded={(arr) => setForm(f => ({ ...f, photos: [...f.photos, ...arr] }))} testid="lost-photo" />
                        <PhotoGrid photos={form.photos} onRemove={(i) => setForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }))} />
                    </div>
                    <Button onClick={submit} disabled={busy || ro} className="bg-amber-600 hover:bg-amber-700" data-testid="lost-submit">{busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}Kaydet</Button>
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Kayıt Listesi</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <div className="flex gap-2 flex-wrap">
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara (eşya/oda/not)" className="w-64" data-testid="lost-q" />
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-40" data-testid="lost-status-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Durumlar</SelectItem>
                                {Object.entries(LOST_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {list.length === 0 ? <div className="text-xs text-slate-400 text-center py-3">Kayıt yok</div> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="lost-list">
                                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                                    <tr><th className="px-2 py-1 text-left">Oda</th><th>Eşya</th><th>Bulan</th><th>Durum</th><th>Tarih</th><th>Aksiyon</th></tr>
                                </thead>
                                <tbody>
                                    {list.map(it => (
                                        <tr key={it.id} className="border-t">
                                            <td className="px-2 py-1 font-mono">{it.room_no || '—'}</td>
                                            <td className="px-2 py-1">{it.item}</td>
                                            <td className="px-2 py-1 text-xs">{it.finder_name}</td>
                                            <td className="px-2 py-1"><Badge className={it.status === 'depoda' ? 'bg-amber-100 text-amber-700' : it.status === 'teslim' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>{LOST_STATUS[it.status]}</Badge></td>
                                            <td className="px-2 py-1 text-xs text-slate-500">{new Date(it.created_at).toLocaleDateString('tr-TR')}</td>
                                            <td className="px-2 py-1">
                                                <Select value={it.status} onValueChange={(v) => updateStatus(it.id, v)}>
                                                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {Object.entries(LOST_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ===== Şikayet Sayfası =====
function GlitchView({ meta, onOpenTicket, reloadKey, refresh }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    useEffect(() => {
        setLoading(true);
        api.get('/operations/requests', { params: { type: 'sikayet' } })
            .then(r => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [reloadKey]);
    const filtered = useMemo(() => applyFilters(items, filters), [items, filters]);
    return (
        <div className="space-y-3">
            <SummaryCards tickets={items} />
            <NewRequestForm meta={meta} onCreated={refresh} defaultType="sikayet" />
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Tüm Şikayetler ({filtered.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <FilterBar filters={filters} setFilters={setFilters} showDep />
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-4" /> : <TicketTable items={filtered} onOpen={onOpenTicket} emptyText="Şikayet kaydı yok" />}
                </CardContent>
            </Card>
        </div>
    );
}

// ===== Raporlar Sayfası =====
function ReportsView() {
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [department, setDepartment] = useState('all');
    const [perf, setPerf] = useState([]);
    const [daily, setDaily] = useState({ done: [], pending: [], overdue: [], totals: {} });
    const [loading, setLoading] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [pdfPeriod, setPdfPeriod] = useState('week');
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const p1 = api.get('/operations/reports/performance', { params: { start: start || undefined, end: end || undefined } });
            const p2 = api.get('/operations/reports/daily', { params: { start: start || undefined, end: end || undefined, department: department === 'all' ? undefined : department } });
            const [r1, r2] = await Promise.all([p1, p2]);
            setPerf(r1.data.items || []);
            setDaily(r2.data || { done: [], pending: [], overdue: [], totals: {} });
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [start, end, department]);
    useEffect(() => { fetchAll(); }, [fetchAll]);
    const downloadPdf = async () => {
        setPdfBusy(true);
        try {
            const params = { period: pdfPeriod };
            if (pdfPeriod === 'range') { params.start = start; params.end = end; }
            const r = await api.get('/operations/analytics/pdf', { params, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url; a.download = `operasyon-raporu-${pdfPeriod}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF indirildi');
        } catch (e) { toast.error(e?.response?.data?.detail || 'PDF indirilemedi'); }
        finally { setPdfBusy(false); }
    };
    return (
        <div className="space-y-3" data-testid="ops-reports-view">
            <Card>
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Filtre & Dışa Aktarım</CardTitle>
                        <div className="flex items-center gap-1 flex-wrap">
                            <Select value={pdfPeriod} onValueChange={setPdfPeriod}>
                                <SelectTrigger className="w-36 h-8" data-testid="rep-pdf-period"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="today">Bugün</SelectItem>
                                    <SelectItem value="week">Bu Hafta</SelectItem>
                                    <SelectItem value="month">Bu Ay</SelectItem>
                                    <SelectItem value="range">Tarih Aralığı</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button size="sm" onClick={downloadPdf} disabled={pdfBusy} className="h-8 bg-indigo-600 hover:bg-indigo-700" data-testid="rep-pdf">
                                {pdfBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}PDF İndir
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="text-xs text-slate-500">Başlangıç</div>
                        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44 h-9" data-testid="rep-start" />
                        <div className="text-xs text-slate-500">Bitiş</div>
                        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44 h-9" data-testid="rep-end" />
                        <Select value={department} onValueChange={setDepartment}>
                            <SelectTrigger className="w-44 h-9" data-testid="rep-dep"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Departmanlar</SelectItem>
                                {Object.entries(DEP_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={fetchAll} disabled={loading} data-testid="rep-apply" className="bg-emerald-600 hover:bg-emerald-700">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}Uygula</Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Departman Performansı</CardTitle></CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="rep-perf-table">
                            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                                <tr>
                                    <th className="px-3 py-2 text-left">Departman</th>
                                    <th className="px-3 py-2">Toplam</th>
                                    <th className="px-3 py-2">Açık</th>
                                    <th className="px-3 py-2">Kapalı</th>
                                    <th className="px-3 py-2">İptal</th>
                                    <th className="px-3 py-2">Ort. Yanıt</th>
                                    <th className="px-3 py-2">Ort. Çözüm</th>
                                    <th className="px-3 py-2">SLA %</th>
                                    <th className="px-3 py-2 text-left">Öncelik</th>
                                </tr>
                            </thead>
                            <tbody>
                                {perf.map(p => (
                                    <tr key={p.department} className="border-t" data-testid={`rep-perf-${p.department}`}>
                                        <td className="px-3 py-2 font-semibold">{DEP_LABELS[p.department] || p.department}</td>
                                        <td className="px-3 py-2 text-center">{p.total}</td>
                                        <td className="px-3 py-2 text-center text-amber-700 font-medium">{p.open}</td>
                                        <td className="px-3 py-2 text-center text-emerald-700 font-medium">{p.closed}</td>
                                        <td className="px-3 py-2 text-center text-slate-500">{p.cancelled}</td>
                                        <td className="px-3 py-2 text-center">{fmtMin(p.avg_response_min)}</td>
                                        <td className="px-3 py-2 text-center">{fmtMin(p.avg_resolution_min)}</td>
                                        <td className="px-3 py-2 text-center">{p.sla_compliance_pct == null ? '—' : <span className={`font-bold ${p.sla_compliance_pct >= 80 ? 'text-emerald-700' : p.sla_compliance_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{p.sla_compliance_pct}%</span>}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {Object.entries(p.by_priority).filter(([, n]) => n > 0).map(([k, n]) => <Badge key={k} className={`mr-1 ${PRIORITY_COLORS[k]}`}>{k}:{n}</Badge>)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card><CardHeader className="pb-2"><CardTitle className="text-base text-emerald-700">Yapılan ({daily.totals.done || 0})</CardTitle></CardHeader>
                    <CardContent className="text-xs space-y-1 max-h-72 overflow-y-auto" data-testid="rep-done">
                        {(daily.done || []).slice(0, 50).map(r => (<div key={r.id} className="border-b pb-1"><b>{r.location}</b> · {r.category} · <span className="text-slate-500">{DEP_LABELS[r.department]}</span></div>))}
                        {!daily.done?.length && <div className="text-slate-400">Kayıt yok</div>}
                    </CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-base text-amber-700">Eksik / Açık ({daily.totals.pending || 0})</CardTitle></CardHeader>
                    <CardContent className="text-xs space-y-1 max-h-72 overflow-y-auto" data-testid="rep-pending">
                        {(daily.pending || []).slice(0, 50).map(r => (<div key={r.id} className="border-b pb-1"><b>{r.location}</b> · {r.category} · <span className="text-slate-500">{DEP_LABELS[r.department]}</span></div>))}
                        {!daily.pending?.length && <div className="text-slate-400">Kayıt yok</div>}
                    </CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-base text-red-700">Geciken / SLA Aşımı ({daily.totals.overdue || 0})</CardTitle></CardHeader>
                    <CardContent className="text-xs space-y-1 max-h-72 overflow-y-auto" data-testid="rep-overdue">
                        {(daily.overdue || []).slice(0, 50).map(r => (<div key={r.id} className="border-b pb-1"><b>{r.location}</b> · {r.category} · <span className="text-slate-500">{DEP_LABELS[r.department]}</span></div>))}
                        {!daily.overdue?.length && <div className="text-slate-400">Kayıt yok</div>}
                    </CardContent></Card>
            </div>
        </div>
    );
}

// ===== Ana Yönlendirici =====
export default function Operations() {
    const { dep: depSlug } = useParams();
    const [meta, setMeta] = useState(null);
    const [openTicket, setOpenTicket] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const depInfo = depSlug ? DEP_MAP[depSlug] : null;

    useEffect(() => { api.get('/operations/meta').then(r => setMeta(r.data)).catch(() => { }); }, []);

    if (!depSlug || !depInfo) return <Navigate to="/operations/order-taker" replace />;

    const refresh = () => setReloadKey(k => k + 1);
    const refreshAndReopen = async (id) => {
        try { const r = await api.get(`/operations/requests`, { params: {} }); const updated = (r.data.items || []).find(x => x.id === id); if (updated) setOpenTicket(updated); }
        catch { /* ignore */ }
        refresh();
    };

    const HeaderIcon = depInfo.icon;
    const subtitle = {
        operator: 'Tüm departmanların komuta merkezi — yeni kayıt, dağıtım, atama, durum yönetimi.',
        dept: 'İş Emri (size atanan işler) · Talepler (sizin açtığınız) · İlgili Şikayetler',
        housekeeping: 'İş Emri · Talepler · İlgili Şikayetler · Odalar (340 oda · 5 kat)',
        glitch: 'Tüm şikayetler — kök neden, çözüm, misafir telafisi ve takip.',
        reports: 'Departman performansı, yapılan / eksik / geciken işler, SLA uyumu.',
    }[depInfo.kind];

    return (
        <div className="space-y-3" data-testid="operations-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    <HeaderIcon className="w-6 h-6 text-emerald-600" /> Operasyon Merkezi · {depInfo.label}
                </h1>
                <p className="text-sm text-slate-500">{subtitle}</p>
            </div>

            {depInfo.kind === 'operator' && <OrderTakerView meta={meta} onOpenTicket={setOpenTicket} reloadKey={reloadKey} refresh={refresh} />}
            {depInfo.kind === 'dept' && <DepartmentView dep={depInfo.value} meta={meta} onOpenTicket={setOpenTicket} reloadKey={reloadKey} refresh={refresh} />}
            {depInfo.kind === 'housekeeping' && (
                <DepartmentView
                    dep="kat_hizmetleri"
                    meta={meta}
                    onOpenTicket={setOpenTicket}
                    reloadKey={reloadKey}
                    refresh={refresh}
                    includeRooms
                    roomsContent={<HousekeepingRoomsPanel meta={meta} refresh={refresh} />}
                    includeInspections
                    inspectionsContent={<InspectionPanel />}
                    includeLostFound
                    lostFoundContent={<LostFoundPanel />}
                />
            )}
            {depInfo.kind === 'glitch' && <GlitchView meta={meta} onOpenTicket={setOpenTicket} reloadKey={reloadKey} refresh={refresh} />}
            {depInfo.kind === 'reports' && <ReportsView />}

            {openTicket && <TicketDetail ticket={openTicket} onClose={() => setOpenTicket(null)} onChanged={() => refreshAndReopen(openTicket.id)} />}
        </div>
    );
}
